import { useEffect, useRef, useState } from "react";
import { Camera, X, Zap } from "lucide-react";

/** Escaneo con la cámara del celular: BarcodeDetector nativo (Android/Chrome) y respaldo @zxing/browser (iOS/Safari, escritorio). */
export default function CamaraEscaner({ onCodigo, onClose }) {
  const videoRef = useRef(null);
  const [error, setError] = useState("");
  const [motor, setMotor] = useState("");
  const [ultimo, setUltimo] = useState("");
  const ultimoRef = useRef({ code: "", at: 0 });

  useEffect(() => {
    let stream, raf, zx, vivo = true;
    const emitir = (code) => {
      const now = Date.now();
      if (!code || (code === ultimoRef.current.code && now - ultimoRef.current.at < 2500)) return;
      ultimoRef.current = { code, at: now };
      setUltimo(code);
      if (navigator.vibrate) navigator.vibrate(60);
      onCodigo(code);
    };
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
        if (!vivo) return;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        if ("BarcodeDetector" in window) {
          const det = new window.BarcodeDetector({ formats: ["code_128", "ean_13", "ean_8", "code_39", "qr_code", "upc_a", "upc_e", "itf"] });
          setMotor("nativo");
          const loop = async () => {
            if (!vivo) return;
            try { const codes = await det.detect(videoRef.current); if (codes[0]?.rawValue) emitir(codes[0].rawValue); } catch {}
            raf = setTimeout(loop, 180);
          };
          loop();
        } else {
          const { BrowserMultiFormatReader } = await import("@zxing/browser");
          zx = new BrowserMultiFormatReader();
          setMotor("zxing");
          await zx.decodeFromStream(stream, videoRef.current, (res) => { if (res?.getText) emitir(res.getText()); });
        }
      } catch (e) {
        setError(e?.name === "NotAllowedError" ? "Permite el acceso a la cámara para escanear." : `No se pudo abrir la cámara (${e?.message || e}). Usa el escáner USB o captura manual.`);
      }
    })();
    return () => { vivo = false; clearTimeout(raf); try { zx?.stopContinuousDecode?.(); } catch {} stream?.getTracks().forEach((t) => t.stop()); };
  }, [onCodigo]);

  return (
    <div className="rounded-2xl overflow-hidden bg-black relative" data-testid="camara-escaner">
      <video ref={videoRef} playsInline muted className="w-full max-h-[46vh] object-cover" />
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center"><div className="w-[78%] h-[38%] border-2 border-brand-amber rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"><div className="h-0.5 bg-brand-amber/80 animate-pulse mt-[50%]" /></div></div>
      <button type="button" data-testid="camara-cerrar" onClick={onClose} className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white/90 text-brand-ink flex items-center justify-center shadow"><X className="w-4 h-4" /></button>
      <div className="absolute bottom-0 inset-x-0 p-2 flex items-center gap-2 text-[11px] text-white bg-gradient-to-t from-black/70 to-transparent">
        <Camera className="w-3.5 h-3.5 text-brand-amber" />
        {error ? <span className="text-rose-200" data-testid="camara-error">{error}</span> : <span>Apunta al código de la etiqueta · {motor === "nativo" ? "lector nativo" : motor === "zxing" ? "lector universal" : "iniciando…"}</span>}
        {ultimo && <span className="ml-auto font-mono flex items-center gap-1 text-emerald-300" data-testid="camara-ultimo"><Zap className="w-3 h-3" /> {ultimo.slice(0, 26)}</span>}
      </div>
    </div>
  );
}
